<template>
  <div
    class="base-search-bar-container"
    :class="{
      'is-grey': isGrey,
    }"
  >
    <BaseIcon name="search" class="base-search-bar-container__icon" :class="{ 'is-focused': isFocused }" />
    <input
      :class="['base-search-bar-container__input', { 'is-focused': isFocused }]"
      :id="name"
      :name="name"
      :placeholder="placeholder"
      :aria-label="ariaLabel || placeholder"
      type="search"
      enterkeyhint="search"
      v-model="localValue"
      :disabled="disabled"
      @input="onInputChanged"
      @focus="toggleIsFocused"
      @blur="toggleIsFocused"
      @keydown.enter="closeKeyboardOnTouch($event.target)"
    />
    <div class="base-search-bar-container__end-adornment-container">
      <BaseIcon
        v-if="localValue"
        name="x"
        class="icon"
        @mousedown.prevent
        @click="onClear"
        v-tooltip="{ content: $t('actions.erase'), placement: 'top', offset: '8px', delay: 400 }"
      />
      <v-popover
        v-if="tooltipText"
        placement="bottom"
        trigger="hover"
        popoverClass="base-search-bar-container__tooltip-popover"
      >
        <BaseIcon name="info" class="icon" />
        <template v-slot:popover>{{ tooltipText }}</template>
      </v-popover>
    </div>
  </div>
</template>
<script>
import DebounceMixin from '@/mixins/debounce-mixin';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { i18n } from '@/plugins/i18n';
import { closeKeyboardOnTouch } from '@/util/search-key';

export default {
  name: 'BaseSearchBarNew', //TODO: rename to BaseSearchBar once all usages are updated
  components: {
    BaseIcon,
  },
  mixins: [DebounceMixin],
  props: {
    name: String,
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: i18n.t('search-bar.placeholder'),
    },
    // Accessible name for the search field. Falls back to the placeholder.
    ariaLabel: {
      type: String,
      default: '',
    },
    tooltipText: String,
    hasDebounce: {
      type: Boolean,
      default: false,
    },
    isGrey: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValue: this.value,
      isFocused: this.value !== '',
    };
  },
  watch: {
    value(newVal) {
      this.localValue = newVal || '';
      this.isFocused = this.localValue !== '';
    },
  },
  methods: {
    closeKeyboardOnTouch,
    onInputChanged() {
      this.emitSearch();
    },
    toggleIsFocused(event) {
      if (event && event.type === 'focus') {
        this.isFocused = true;
      } else if (event && event.type === 'blur') {
        this.isFocused = this.localValue !== '';
      }
    },
    onClear() {
      if (this.localValue !== '') {
        this.localValue = '';
        this.isFocused = false;

        this.$emit('input', '');
      }
    },
    emitSearch() {
      const v = this.localValue;

      const emit = () => {
        this.$emit('input', v);
      };

      this.hasDebounce ? this.debounce(emit, 1000) : emit();
    },
  },
};
</script>
<style lang="scss">
.base-search-bar-container {
  position: relative;
  width: 100%;
  background-color: $color-slate-50;
  border-radius: $border-radius-root;

  &.is-grey {
    background-color: $color-neutral-pale;
  }

  &__icon {
    color: $color-normal-pale;
    width: 16px;
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);

    &.is-focused {
      color: $color-accent;
    }
  }

  &__input {
    width: 100%;
    padding: 12px;
    padding-left: 40px;
    padding-right: 70px;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    color: $color-normal;
    font-size: 14px;
    transition: border 200ms ease, box-shadow 200ms ease;

    // No hover border-darkening: the field already shows a border at rest and an accent border on
    // focus, so the extra hover darkening (FormInput does it) reads too heavy on a field this size.
    // Filled or focused shows the accent border (existing behavior); the focus glow matches
    // FormInput and only appears on real focus, so a filled-but-blurred bar doesn't glow.
    &:focus,
    &:focus-visible,
    &.is-focused {
      outline: none;
      border-color: $color-accent;
    }

    &:focus,
    &:focus-visible {
      box-shadow: 0 0 0 3px rgba($color-accent-500, 0.1);
    }

    &::placeholder {
      color: $color-text-muted;
      font-style: normal;
    }
  }

  &__end-adornment-container {
    position: absolute;
    top: 50%;
    right: 15px;
    transform: translateY(-50%);
    display: flex;
    column-gap: 10px;

    .icon {
      color: $color-normal;
      height: 16px;
      width: 16px;
      cursor: pointer;
    }

    // override v-popover trigger style
    .v-popover .trigger {
      display: flex;
    }
  }

  &__tooltip-popover {
    padding: 8px;
    font-size: $font-size-xsmall;
    text-align: center;
    background-color: $color-normal !important;
    color: $color-white !important;
    box-shadow: $shadow-tooltip;
    max-width: 400px;

    .popover-arrow {
      background-color: $color-normal !important;
    }
  }
}
</style>
